<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_reports_title')"
        :subtitle="t('vue.dashboard_overview_reports_subtitle')"
        :link-href="settingsHref"
        :link-label="t('vue.dashboard_overview_reports_link')"
    >
        <div
            class="mb-4 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
        >
            <span class="text-[13px] leading-[18px] text-gray-600">
                {{
                    t("vue.dashboard_overview_reports_active_count", {
                        active: overview.activeCount,
                        total: overview.totalCount,
                    })
                }}
            </span>
            <DashboardStatusBadge
                :status="overview.active ? 'active' : 'disabled'"
                :label="
                    overview.active
                        ? t('vue.dashboard_overview_state_active')
                        : t('vue.dashboard_overview_state_inactive')
                "
            />
        </div>

        <p v-if="overview.schedules.length === 0" class="text-sm text-gray-600">
            {{ t("vue.dashboard_overview_reports_empty") }}
        </p>

        <div
            v-for="schedule in overview.schedules"
            v-else
            :key="schedule.name"
            class="flex min-h-[52px] items-center gap-3 border-t border-gray-200 py-2 first:border-t-0 first:pt-0"
        >
            <div
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12.8px] bg-gray-100"
            >
                <EnvelopeIcon class="h-[18px] w-[18px] text-gray-500" />
            </div>
            <div class="min-w-0 flex-1">
                <p class="truncate text-[13px] font-medium text-gray-900">
                    {{ schedule.name }}
                </p>
                <p class="truncate text-[12px] leading-[16px] text-gray-500">
                    {{ resolveMeta(schedule) }}
                </p>
            </div>
            <div class="shrink-0 text-right">
                <p
                    class="text-[12px] font-medium"
                    :class="
                        schedule.active ? 'text-green-600' : 'text-gray-400'
                    "
                >
                    {{
                        schedule.active
                            ? t("vue.dashboard_overview_state_active")
                            : t("vue.dashboard_overview_state_inactive")
                    }}
                </p>
                <p
                    v-if="schedule.active && schedule.nextSendAt !== null"
                    class="text-[12px] text-gray-400"
                >
                    {{
                        t("vue.dashboard_overview_reports_next_send", {
                            time: formatRelativeTime(schedule.nextSendAt),
                        })
                    }}
                </p>
            </div>
        </div>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { EnvelopeIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import DashboardStatusBadge from "./DashboardStatusBadge.vue";
import { useDashboardRelativeTime } from "../composables/useDashboardRelativeTime";
import type {
    IEmailReportSchedule,
    IEmailReportingOverview,
} from "@/types/Dashboard/IDashboardOverview";

defineProps<{
    overview: IEmailReportingOverview;
}>();

const { t } = useTranslations();
const { formatRelativeTime } = useDashboardRelativeTime();
const page = usePage<IRhizomPageProps>();

const settingsHref = computed<string | null>(() =>
    page.props.hasFreePlan ? null : route("reports.settings.index"),
);

const resolveMeta = (schedule: IEmailReportSchedule): string =>
    [
        t(`vue.dashboard_overview_reports_frequency_${schedule.frequency}`),
        t("vue.dashboard_overview_reports_recipients", {
            count: schedule.recipientCount,
        }),
        schedule.coversAllDomains
            ? t("vue.dashboard_overview_reports_all_domains")
            : t("vue.dashboard_overview_reports_domains", {
                  count: schedule.domainCount,
              }),
    ].join(" · ");
</script>
